import { CheckCircle, AlertTriangle, Loader2, Circle } from 'lucide-react';
import type { StepState, StepId } from '../reconciliation-types';

interface StepIndicatorProps {
  steps: StepState[];
  activeStep: StepId;
  onStepClick: (stepId: StepId) => void;
}

const statusIcon = (status: StepState['status'], isActive: boolean) => {
  switch (status) {
    case 'done': return <CheckCircle className="w-5 h-5 text-success" />;
    case 'warning': return <AlertTriangle className="w-5 h-5 text-warning" />;
    case 'error': return <AlertTriangle className="w-5 h-5 text-danger" />;
    case 'loading': return <Loader2 className="w-5 h-5 text-accent-primary animate-spin" />;
    default: return <Circle className={`w-5 h-5 ${isActive ? 'text-accent-primary' : 'text-text-muted'}`} />;
  }
};

export function StepIndicator({ steps, activeStep, onStepClick }: StepIndicatorProps) {
  return (
    <div className="flex items-center gap-1 p-2 rounded-xl bg-surface-raised/50 border border-border-subtle">
      {steps.map((step, i) => {
        const isActive = step.id === activeStep;
        return (
          <div key={step.id} className="flex items-center flex-1">
            <button
              onClick={() => onStepClick(step.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all w-full ${
                isActive
                  ? 'bg-surface-hover border border-accent-primary/30 shadow-lg shadow-accent-primary/20/5'
                  : 'hover:bg-surface-hover/50'
              }`}
            >
              <span className="flex-shrink-0">{statusIcon(step.status, isActive)}</span>
              <div className="text-left min-w-0">
                <div className={`text-xs font-medium truncate ${isActive ? 'text-accent-primary' : 'text-text-secondary'}`}>
                  {i + 1}. {step.label}
                </div>
                {step.summary && (
                  <div className="text-[10px] text-text-muted truncate">{step.summary}</div>
                )}
              </div>
            </button>
            {i < steps.length - 1 && (
              <div className={`w-6 h-px mx-1 flex-shrink-0 ${step.status === 'done' ? 'bg-success/30' : 'bg-surface-hover/60'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}
